import React, {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Spinner from 'src/components/library/spinner/Spinner';
import MenuItem from 'src/components/library/menu-item/LytMenuItem';
import LytButton from 'src/components/library/button/LytButton';
import LytCheckbox from 'src/components/library/check-box/LytCheckbox';
import LytRadio from 'src/components/library/radio/LytRadio';
import useStableId from 'src/hooks/useStableId';
import LytTextField from 'src/components/library/text-field/LytTextField';
import { shadowMixin } from 'src/components/styled/theme/shadows';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from 'src/components/styled/theme/typography-utils';
const DropdownChevron = styled.svg`
    width: 13px;
    height: 8px;
    flex-shrink: 0;

    path {
        fill: currentColor;
    }
`;
const DropdownContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    ${({ $fullWidth }) =>
        $fullWidth &&
        `
        width: 100%;
    `}
`;
const DropdownContent = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 244px;
    -webkit-transform: translateY(42px);
    -ms-transform: translateY(42px);
    transform: translateY(42px);
    background: ${({ theme }) =>
        theme.components.lyt_dropdown.content.background};
    border-radius: 4px;
    z-index: 1000;
    ${shadowMixin.S}
    padding: 8px 0px;
    ${({ $fullWidth }) =>
        $fullWidth &&
        `
        width: 100%;
        right: auto;
        left: 0;
    `}
`;
const FloatingDropdownContent = styled.div`
    position: fixed;
    width: 244px;
    background: ${({ theme }) =>
        theme.components.lyt_dropdown.content.background};
    border-radius: 4px;
    z-index: 10000;
    ${shadowMixin.S}
    padding: 16px 0px;
    pointer-events: auto;
`;
const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 10px;
`;
const DropdownTrigger = styled.div`
    ${({ $fullWidth }) =>
        $fullWidth &&
        `
        width: 100%;
        
        button {
            width: 100%;
            justify-content: space-between;
        }
    `}
`;
const MenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-height: ${({ $menuMaxHeight }) => $menuMaxHeight}px;
    overflow-y: auto;
`;
const HeaderContainer = styled.div`
    padding: 0 8px 8px 8px;
    border-bottom: 2px solid
        ${({ theme }) => theme.components.lyt_input.border.default};
`;
const GroupHeader = styled.div`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_BOLD)}
    color: ${({ theme }) => theme.text.default};
    padding: 16px 16px 8px 16px;
    cursor: default;
    user-select: none;
`;
const OptionSubtext = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme.text.subtle};
    margin-top: 2px;
`;
const SelectionItemContent = styled.span`
    display: inline-flex;
    flex-direction: column;
`;
const PlainItemContent = styled.div`
    display: flex;
    flex-direction: column;
`;
const PresentationalItem = styled.div`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme.text.default};
    display: flex;
    min-height: 40px;
    padding: 8px 16px;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    cursor: default;
    user-select: none;
`;

/**
 * @description Gets the text to display in the dropdown
 * @param {array} options - The options to display in the dropdown
 * @param {object} selectedOptions - The selected options in the dropdown
 * @param {string} placeholder - The placeholder text of the dropdown
 * @returns {string} - The text to display in the dropdown
 */
const getDropdownText = (options, selectedOptions, placeholder) => {
    if (!options?.length) {
        return placeholder;
    }

    const selectedText = Object.entries(selectedOptions || {})
        .filter(([, selected]) => selected)
        .map(([selectedId]) =>
            options.find(
                (option) => String(option?.id) === String(selectedId)
            )?.text
        )
        .filter(Boolean);

    return selectedText.length ? selectedText.join(', ') : placeholder;
};

/**
 * @description The dropdown component
 * @param {string} id - The id of the dropdown
 * @param {string} name - The name of the dropdown
 * @param {string} dropdownPlaceholder - The placeholder text of the dropdown
 * @param {array} options - The options to display in the dropdown
 * @param {object} selectedOptions - The selected options in the dropdown
 * @param {boolean} loading - Whether the dropdown is loading (spinner inside the open menu)
 * @param {boolean} disabled - Disables the trigger and prevents the menu from opening
 * @param {boolean} triggerLoading - Shows a spinner in the trigger and prevents the menu from opening (e.g. while options load)
 * @param {function} onChange - The function to call when an item is clicked
 * @param {boolean} multiselect - Whether the dropdown is multiselect (shows checkboxes)
 * @param {boolean} searchable - Whether the dropdown is searchable
 * @param {string} searchPlaceholder - The placeholder text of the search input
 * @param {boolean} showSelectionIndicator - Whether to show checkboxes (multiselect) or radio buttons (single-select) in menu items
 * @param {React.ReactNode} children - Custom trigger element (button, icon, etc.) If provided, will be used instead of default dropdown button
 * @param {string} triggerIcon - Icon name for icon-only trigger (no text, no chevron). When set, creates an icon-only button trigger
 * @param {string} triggerType - Button type for the trigger (defaults to "secondary")
 * @param {boolean} hideChevron - Whether to hide the chevron icon on the default trigger
 * @param {boolean} usePortal - Whether to render dropdown content in a portal for proper z-index stacking (useful for tables, modals)
 * @param {('left'|'right')} portalAlign - Horizontal alignment for portal dropdown content relative to trigger (defaults to right)
 * @param {React.ReactNode} menuHeader - Optional non-interactive content rendered at the top of the menu, before options
 * @param {number} menuMaxHeight - Maximum height of the scrollable options area
 * @param {string} triggerText - Optional override for the trigger button display text. When provided, takes precedence over the auto-computed text from selectedOptions.
 * @param {string} triggerAriaLabel - Accessible name for the dropdown trigger.
 * @param {string} triggerAriaLabelledBy - ID of the visible label naming the dropdown trigger.
 */
const LytDropdown = ({
    id = '',
    name = '',
    dropdownPlaceholder = '',
    selectedOptions = {},
    options = [],
    loading = false,
    disabled = false,
    triggerLoading = false,
    onChange = () => {},
    multiselect = false,
    searchable = false,
    searchPlaceholder = '',
    fullWidth = false,
    children,
    showSelectionIndicator = false,
    triggerIcon = undefined,
    triggerType = 'secondary',
    hideChevron = false,
    usePortal = false,
    portalAlign = 'right',
    menuHeader = undefined,
    menuMaxHeight = 200,
    triggerText = undefined,
    triggerAriaLabel = undefined,
    triggerAriaLabelledBy = undefined,
}) => {
    const dropdownId = useStableId('dropdown', id);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [focusTick, setFocusTick] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: -9999,
        left: -9999,
        width: 160,
    });
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownContentRef = useRef(null);
    const menuContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const localSelectedOptions = selectedOptions || {};
    const triggerBlocked = disabled || triggerLoading;

    // Close an open menu if the trigger becomes disabled or starts loading.
    useEffect(() => {
        if (triggerBlocked) {
            setIsOpen(false);
        }
    }, [triggerBlocked]);
    const dropdownRoot = document.getElementById('dropdown-root');

    // Determine if options use grouping (any option has a `group` field)
    const hasGroups = options.some((option) => option.group);

    /**
     * Calculate dropdown position relative to trigger for portal mode.
     * Default alignment is right to preserve existing behavior.
     */
    const updateDropdownPosition = useCallback(() => {
        if (!triggerRef.current) return;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Determine dropdown width - match trigger width if fullWidth, otherwise use fixed 244px
        const dropdownWidth = fullWidth ? triggerRect.width : 244;

        // Default position: below the trigger, configurable horizontal alignment.
        let top = triggerRect.bottom + 4;
        let left = portalAlign === 'left'
            ? triggerRect.left
            : triggerRect.right - dropdownWidth;

        // If dropdown would go off the left edge, keep it at left edge of viewport
        if (left < 8) {
            left = 8;
        }

        // If dropdown would go off the right edge, adjust left
        if (left + dropdownWidth > viewportWidth - 8) {
            left = viewportWidth - dropdownWidth - 8;
        }

        // If dropdown would go off the bottom, position above the trigger
        // Estimate dropdown height (max 200px for menu)
        const estimatedDropdownHeight = Math.min(
            options.length * 36 + 16,
            menuMaxHeight,
        );
        if (top + estimatedDropdownHeight > viewportHeight - 8) {
            top = triggerRect.top - estimatedDropdownHeight - 4;
        }
        setDropdownPosition({
            top,
            left,
            width: dropdownWidth,
        });
    }, [options.length, fullWidth, menuMaxHeight, portalAlign]);

    /**
     * handles collapsing the dropdown when user clicks outside the dropdown
     */
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is inside container
            if (
                containerRef.current &&
                containerRef.current.contains(event.target)
            ) {
                return;
            }
            // Check if click is inside portal dropdown content
            if (
                dropdownContentRef.current &&
                dropdownContentRef.current.contains(event.target)
            ) {
                return;
            }
            setIsOpen(false);
            setSearchText('');
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * Update position when opening in portal mode
     */
    useLayoutEffect(() => {
        if (isOpen && usePortal) {
            updateDropdownPosition();
        }
    }, [isOpen, usePortal, updateDropdownPosition]);

    /**
     * Handles the dropdown click event
     */
    const handleDropdownClick = () => {
        if (triggerBlocked) {
            return;
        }
        // Calculate position immediately before opening to prevent flash at (0,0)
        if (!isOpen && usePortal) {
            updateDropdownPosition();
        }
        if (isOpen) {
            setSearchText('');
            setFocusedIndex(-1);
        } else {
            setSearchText('');
            if (options.length === 0) {
                return;
            }
            setFocusedIndex(searchable ? -2 : 0);
        }
        setIsOpen(!isOpen);
    };

    /**
     * Handles the item click event for the dropdown
     * @param {string} itemId - The id of the item to be selected
     * @param {Event} event - The click event
     */
    const handleItemClick = (itemId, event) => {
        let newSelectedOptions = {
            ...localSelectedOptions,
        };

        // In multiselect mode, if the click came from the checkbox itself,
        // the checkbox's onChange handler will update the state
        if (multiselect && event?.target?.type === 'checkbox') {
            // Let the checkbox handle its own state change
            return;
        }
        if (multiselect) {
            if (newSelectedOptions[itemId]) {
                delete newSelectedOptions[itemId];
            } else {
                newSelectedOptions[itemId] = true;
            }
        } else {
            newSelectedOptions = {
                [itemId]: true,
            };
            // Close dropdown for single-select
            setIsOpen(false);
            setSearchText('');
        }
        onChange(newSelectedOptions);
    };

    /**
     * Handles checkbox changes in multiselect mode
     * @param {Event} event - The change event from the checkbox
     * @param {string} itemId - The id of the item
     */
    const handleCheckboxChange = (event, itemId) => {
        if (!multiselect) {
            // Single-select radio: replace the entire selection and close the dropdown,
            // mirroring what handleItemClick does for plain items. We handle it here
            // because LytRadio has stopPropagation={true}, so the click never reaches
            // LytMenuItem and handleItemClick never fires.
            if (event.target.checked) {
                onChange({
                    [itemId]: true,
                });
                setIsOpen(false);
                setSearchText('');
            }
            return;
        }

        // Multiselect checkbox: add or remove the item from the selection
        const newSelectedOptions = {
            ...localSelectedOptions,
        };
        if (event.target.checked) {
            newSelectedOptions[itemId] = true;
        } else {
            delete newSelectedOptions[itemId];
        }
        onChange(newSelectedOptions);
    };

    /**
     * Handles the search input change event for the dropdown
     * @param {string} value - The value of the search input
     */
    const handleSearchInputChange = (value) => {
        setSearchText(value);
    };

    /**
     * Move focus to the menu item at focusedIndex whenever it changes.
     * A value of -2 is a sentinel that means "focus the search input".
     */
    useEffect(() => {
        if (!isOpen) return;
        if (focusedIndex === -2 && searchInputRef.current) {
            searchInputRef.current.focus();
            return;
        }
        if (focusedIndex < 0) return;
        const container = menuContainerRef.current;
        if (!container) return;
        const items = container.querySelectorAll(
            '[role="menuitem"]:not([aria-disabled="true"])'
        );
        if (items[focusedIndex]) {
            items[focusedIndex].focus();
        }
    }, [focusedIndex, focusTick, isOpen]);

    /**
     * Handles keyboard navigation within the dropdown menu.
     * ArrowDown / ArrowUp move focus, Home / End jump to first / last,
     * Escape closes the dropdown.
     */
    const handleMenuKeyDown = useCallback((event) => {
        const container = menuContainerRef.current;
        if (!container) return;
        const items = container.querySelectorAll(
            '[role="menuitem"]:not([aria-disabled="true"])'
        );
        const count = items.length;
        if (count === 0) return;
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                setFocusedIndex((prev) => (prev + 1) % count);
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                setFocusedIndex((prev) => {
                    if (prev <= 0 && searchInputRef.current) {
                        return -2; // sentinel: let the effect focus the search input
                    }
                    return (prev - 1 + count) % count;
                });
                break;
            }
            case 'Home': {
                event.preventDefault();
                setFocusedIndex(0);
                break;
            }
            case 'End': {
                event.preventDefault();
                setFocusedIndex(count - 1);
                break;
            }
            case 'Escape': {
                event.preventDefault();
                setIsOpen(false);
                setSearchText('');
                setFocusedIndex(-1);
                // Return focus to the trigger
                if (triggerRef.current) {
                    const trigger =
                        triggerRef.current.querySelector('button') ||
                        triggerRef.current;
                    trigger.focus();
                }
                break;
            }
            default:
                break;
        }
    }, []);
    const dropdownText =
        triggerText ||
        getDropdownText(options, localSelectedOptions, dropdownPlaceholder);
    const filteredOptions = searchable
        ? options.filter((option) =>
              String(option.text ?? '')
                  .toLowerCase()
                  .includes(String(searchText ?? '').toLowerCase())
          )
        : options;

    /**
     * Builds the label node for a selection indicator item.
     * When the option has subtext, returns a stacked label+subtext node;
     * otherwise returns the plain text string.
     */
    const buildSelectionLabel = (option) => {
        if (!option.subtext) return option.text;
        return (
            <SelectionItemContent>
                <span>{option.text}</span>
                <OptionSubtext>{option.subtext}</OptionSubtext>
            </SelectionItemContent>
        );
    };

    /**
     * Renders a single option menu item (reused in both flat and grouped rendering)
     */
    const renderOption = (option) => (
        <MenuItem
            key={option.id}
            id={option.id}
            onClick={handleItemClick}
            selected={!!localSelectedOptions[option.id]}
            disabled={option.disabled}
            danger={option.danger}
            tooltip={option.tooltip}>
            {showSelectionIndicator ? (
                multiselect ? (
                    <LytCheckbox
                        id={option.id}
                        checked={!!localSelectedOptions[option.id]}
                        onChange={(e) => handleCheckboxChange(e, option.id)}
                        label={buildSelectionLabel(option)}
                        ariaLabel={option.text}
                        stopPropagation={true}
                    />
                ) : (
                    <LytRadio
                        id={option.id}
                        name={`${dropdownId}-radio-group`}
                        checked={!!localSelectedOptions[option.id]}
                        onChange={(e) => handleCheckboxChange(e, option.id)}
                        label={buildSelectionLabel(option)}
                        ariaLabel={option.text}
                        stopPropagation={true}
                    />
                )
            ) : option.subtext ? (
                <PlainItemContent>
                    <span>{option.text}</span>
                    <OptionSubtext>{option.subtext}</OptionSubtext>
                </PlainItemContent>
            ) : (
                option.text
            )}
        </MenuItem>
    );

    /**
     * Renders filtered options with group headers when options have a `group` field.
     * Groups are derived from the option order — a new header is inserted whenever
     * the `group` value changes between consecutive options.
     */
    const renderMenuItems = () => {
        if (filteredOptions.length === 0) {
            if (!searchable) {
                return null;
            }
            return (
                <MenuItem id={`${dropdownId}-no-results`} onClick={() => {}}>
                    No results found
                </MenuItem>
            );
        }
        if (!hasGroups) {
            return filteredOptions.map(renderOption);
        }

        // Insert group headers by tracking the current group label
        let currentGroup = null;
        return filteredOptions.reduce((items, option) => {
            if (option.group && option.group !== currentGroup) {
                currentGroup = option.group;
                items.push(
                    <GroupHeader
                        key={`group-${currentGroup}`}
                        role="presentation">
                        {currentGroup}
                    </GroupHeader>
                );
            }
            items.push(renderOption(option));
            return items;
        }, []);
    };

    /**
     * Renders the appropriate trigger element based on props
     * Priority: children > triggerIcon > default button with text
     */
    const renderTrigger = () => {
        // Custom trigger element takes highest priority
        if (children) {
            return children;
        }

        // Icon-only trigger mode
        if (triggerIcon) {
            return (
                <LytButton
                    type={triggerType}
                    icon={triggerIcon}
                    disabled={disabled}
                    loading={triggerLoading}
                    selected={isOpen}
                    aria-label={triggerAriaLabel}
                    aria-labelledby={triggerAriaLabelledBy}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                />
            );
        }

        // Default dropdown button with text and optional chevron
        return (
            <LytButton
                type={triggerType}
                disabled={disabled}
                loading={triggerLoading}
                rightElement={
                    !hideChevron && (
                        <DropdownChevron viewBox="0 0 13 8" aria-hidden="true">
                            <path d="M6.91403 6.91403C6.52353 7.30463 5.89053 7.30463 5.50003 6.91403L3.24249e-05 1.41406L1.41403 0L6.20703 4.79293L11 0L12.4141 1.41406L6.91403 6.91403Z" />
                        </DropdownChevron>
                    )
                }
                aria-label={triggerAriaLabel}
                aria-labelledby={triggerAriaLabelledBy}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                selected={isOpen}>
                {dropdownText}
            </LytButton>
        );
    };

    /**
     * Renders the dropdown menu content (options list)
     */
    const renderDropdownContent = () => {
        return (
            <>
                {searchable && !loading && (
                    <HeaderContainer>
                        <LytTextField
                            ref={searchInputRef}
                            id={`${dropdownId}-dropdown-search`}
                            type="text"
                            name={`${name}-dropdown-search`}
                            placeholder={searchPlaceholder}
                            onChange={handleSearchInputChange}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setFocusedIndex(0);
                                    setFocusTick((t) => t + 1);
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setIsOpen(false);
                                    setSearchText('');
                                    setFocusedIndex(-1);
                                    if (triggerRef.current) {
                                        const trigger =
                                            triggerRef.current.querySelector(
                                                'button'
                                            ) || triggerRef.current;
                                        trigger.focus();
                                    }
                                }
                            }}
                        />
                    </HeaderContainer>
                )}
                {loading ? (
                    <LoadingContainer data-testid="loading-container">
                        <Spinner size="medium" />
                    </LoadingContainer>
                ) : (
                    <MenuContainer
                        ref={menuContainerRef}
                        $menuMaxHeight={menuMaxHeight}
                        role="menu"
                        onKeyDown={handleMenuKeyDown}>
                        {menuHeader && (
                            <PresentationalItem role="presentation">
                                {menuHeader}
                            </PresentationalItem>
                        )}
                        {renderMenuItems()}
                    </MenuContainer>
                )}
            </>
        );
    };
    return (
        <DropdownContainer ref={containerRef} $fullWidth={fullWidth}>
            <DropdownTrigger
                ref={triggerRef}
                $fullWidth={fullWidth}
                onClick={handleDropdownClick}>
                {renderTrigger()}
            </DropdownTrigger>
            {isOpen &&
                (usePortal && dropdownRoot ? (
                    createPortal(
                        <FloatingDropdownContent
                            ref={dropdownContentRef}
                            style={{
                                top: dropdownPosition.top,
                                left: dropdownPosition.left,
                                width: dropdownPosition.width,
                                minWidth: dropdownPosition.width,
                            }}
                            data-dropdown-portal>
                            {renderDropdownContent()}
                        </FloatingDropdownContent>,
                        dropdownRoot
                    )
                ) : (
                    <DropdownContent $fullWidth={fullWidth}>
                        {renderDropdownContent()}
                    </DropdownContent>
                ))}
        </DropdownContainer>
    );
};
LytDropdown.propTypes = {
    searchable: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            text: PropTypes.string.isRequired,
            subtext: PropTypes.string,
            disabled: PropTypes.bool,
            tooltip: PropTypes.string,
            group: PropTypes.string,
        })
    ).isRequired,
    type: PropTypes.string,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    triggerLoading: PropTypes.bool,
    onChange: PropTypes.func,
    multiselect: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    selectedOptions: PropTypes.object,
    dropdownPlaceholder: PropTypes.string,
    fullWidth: PropTypes.bool,
    children: PropTypes.node,
    showSelectionIndicator: PropTypes.bool,
    triggerIcon: PropTypes.string,
    triggerType: PropTypes.oneOf([
        'primary',
        'secondary',
        'tertiary',
        'invisible',
    ]),
    hideChevron: PropTypes.bool,
    usePortal: PropTypes.bool,
    portalAlign: PropTypes.oneOf(['left', 'right']),
    menuHeader: PropTypes.node,
    menuMaxHeight: PropTypes.number,
    triggerText: PropTypes.string,
    triggerAriaLabel: PropTypes.string,
    triggerAriaLabelledBy: PropTypes.string,
};
export default LytDropdown;
